import React, { useEffect, useState } from "react";
import { Download, RefreshCw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { applyServiceWorkerUpdate } from "@/pwa/registerServiceWorker.js";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator.standalone === true;

const PwaInstallAndUpdatePrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [updateRegistration, setUpdateRegistration] = useState(null);
  const [dismissedInstall, setDismissedInstall] = useState(
    () => localStorage.getItem("hotelPwaInstallDismissed") === "true",
  );

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    const handleUpdateAvailable = (event) => {
      setUpdateRegistration(event.detail?.registration || null);
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setDismissedInstall(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("hotel:pwaUpdateAvailable", handleUpdateAvailable);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("hotel:pwaUpdateAvailable", handleUpdateAvailable);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;

    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
    setDismissedInstall(true);
    localStorage.setItem("hotelPwaInstallDismissed", "true");
  };

  const handleDismissInstall = () => {
    setDismissedInstall(true);
    localStorage.setItem("hotelPwaInstallDismissed", "true");
  };

  const showInstall = installPrompt && !dismissedInstall && !isStandalone();
  const showUpdate = Boolean(updateRegistration);

  if (!showInstall && !showUpdate) return null;

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] right-[max(1rem,env(safe-area-inset-right))] z-[80] max-w-sm rounded-2xl border border-white/70 bg-white/95 p-4 text-sm shadow-xl backdrop-blur sm:right-auto">
      {showUpdate ? (
        <div className="space-y-3">
          <div>
            <p className="font-semibold text-slate-900">Update available</p>
            <p className="mt-1 text-xs text-slate-500">
              A newer version of the hotel app is ready.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={() => setUpdateRegistration(null)}
            >
              Later
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-2 rounded-xl"
              onClick={() => applyServiceWorkerUpdate(updateRegistration)}
            >
              <RefreshCw className="h-4 w-4" />
              Update now
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex min-w-0 gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Download className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">Install hotel app</p>
                <p className="mt-1 text-xs text-slate-500">
                  Add the admin panel to this device for quicker department alerts.
                </p>
              </div>
              <Button
                type="button"
                onClick={handleDismissInstall}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Dismiss install prompt"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Button
              type="button"
              size="sm"
              className="mt-3 rounded-xl"
              onClick={handleInstall}
            >
              Install app
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PwaInstallAndUpdatePrompt;
